import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import type { ApiClient } from "./api/client";
import { routeWarmTargets } from "./api/routeWarming";
import { AnalyticsLayout } from "./components/shell/AnalyticsLayout";
import { FEATURES } from "./config/features";
import { requireFeature } from "./config/requireFeature";
import { LoginCallback } from "./auth/LoginCallback";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { validateGlobalFilters } from "./lib/globalFilters";
import { AccountsPage } from "./pages/Accounts";
import { AlertRulesPage } from "./pages/AlertRules";
import { AlertsPage } from "./pages/Alerts";
import { AnalyticsPage } from "./pages/Analytics";
import { CalendarPage } from "./pages/Calendar";
import { ComingSoonPage } from "./pages/ComingSoon";
import { CreateTradePage } from "./pages/CreateTrade";
import { DashboardPage } from "./pages/Dashboard";
import { DemoPage } from "./pages/Demo";
import {
  DisclaimerPage,
  PrivacyPage,
  TermsPage,
} from "./pages/legal/LegalDocumentPage";
import { EquityCurvePage } from "./pages/EquityCurve";
import { HomePage } from "./pages/Home";
import { ImportsNewPage } from "./pages/ImportsNew";
import { InstrumentsPage } from "./pages/Instruments";
import { NotesPage } from "./pages/Notes";
import { NotFoundPage } from "./pages/NotFound";
import { OverallStatisticsPage } from "./pages/OverallStatistics";
import { ProfitLossChartsPage } from "./pages/ProfitLossCharts";
import { SettingsPage } from "./pages/Settings";
import { StrategiesPage } from "./pages/Strategies";
import { TagAnalyticsPage } from "./pages/TagAnalytics";
import { TagsPage } from "./pages/Tags";
import { TradeAnalyticsPage } from "./pages/TradeAnalytics";
import { TradeDetailPage } from "./pages/TradeDetail";
import { TradesPage } from "./pages/Trades";
import { TradeWhatIfPage } from "./pages/TradeWhatIf";

// Router context carries the React Query client and the (auth-aware) API client
// so route loaders -- which run outside React and cannot call hooks -- can warm
// a destination's data before committing the navigation. The real instances are
// supplied by <RouterProvider context={...}> in App; the placeholder below just
// satisfies the type at module load.
export interface RouterContext {
  queryClient: QueryClient;
  apiClient: ApiClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

// Block a navigation until the destination's landing queries are warm, so the
// router keeps the current page on screen (with the global loading cue) and
// swaps only once the next page can render fully -- no skeleton flash. Errors
// resolve too (prefetchQuery never rejects); the page's own error state shows.
async function warmRoute(
  context: RouterContext,
  route: string,
  filters: { since?: string; until?: string; account_id?: string; tag_id?: string },
): Promise<void> {
  await Promise.all(
    routeWarmTargets(context.apiClient, route, filters).map((target) =>
      context.queryClient.prefetchQuery(target),
    ),
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/callback",
  component: LoginCallback,
});

// Public legal pages (L1-U3, ADR 0028 D4). Top-level routes so the documents are
// reachable before sign-in and from the acceptance gate's "read" links, outside
// the authenticated ProtectedRoute layout.
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const disclaimerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/disclaimer",
  component: DisclaimerPage,
});

const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: ProtectedRoute,
  validateSearch: validateGlobalFilters,
  // Filters are per-page now (see usePageFilters). We deliberately do NOT retain
  // since/until/account_id/tag_id across navigation: retaining them re-injected
  // any key the user tried to clear (so "All", chip removal, and "Clear all"
  // silently failed) and made one page's filter leak into the next. Each page
  // restores its own remembered filters on entry instead.
});

type TradeSortKey = "created_at" | "updated_at" | "opened_at" | "closed_at";
type TradeSortOrder = "asc" | "desc";

type TradesSearch = {
  limit?: number;
  offset?: number;
  screen_id?: string;
  sort_key?: TradeSortKey;
  sort_order?: TradeSortOrder;
  // Global date window (since/until). Preserved through validation so the
  // toolbar filter and the dashboard "View all" drill-through (L0-U16) narrow
  // the trade list to the same range the dashboard is showing.
  since?: string;
  until?: string;
  // Origin breadcrumb: when the list was opened from a calendar day-click we
  // remember the origin (the Calendar page or the dashboard's calendar widget)
  // plus the year/month so the user can return to the exact same view.
  from?: "calendar" | "dashboard";
  cal_y?: number;
  cal_m?: number;
};

type CalendarSearch = {
  y?: number;
  m?: number;
  view?: "month" | "year";
};

const UUID_RE_TRADES = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TRADE_SORT_KEYS: readonly TradeSortKey[] = [
  "created_at",
  "updated_at",
  "opened_at",
  "closed_at",
];

const tradesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/trades",
  component: TradesPage,
  validateSearch: (search: Record<string, unknown>): TradesSearch => {
    const result: TradesSearch = {};
    if (typeof search.limit === "number") result.limit = search.limit;
    if (typeof search.offset === "number") result.offset = search.offset;
    if (typeof search.screen_id === "string" && UUID_RE_TRADES.test(search.screen_id)) {
      result.screen_id = search.screen_id;
    }
    if (
      typeof search.sort_key === "string" &&
      TRADE_SORT_KEYS.includes(search.sort_key as TradeSortKey)
    ) {
      result.sort_key = search.sort_key as TradeSortKey;
    }
    if (search.sort_order === "asc" || search.sort_order === "desc") {
      result.sort_order = search.sort_order;
    }
    const dateWindow = validateGlobalFilters(search);
    if (dateWindow.since !== undefined) result.since = dateWindow.since;
    if (dateWindow.until !== undefined) result.until = dateWindow.until;
    if (search.from === "calendar" || search.from === "dashboard") {
      result.from = search.from;
    }
    if (typeof search.cal_y === "number" && Number.isInteger(search.cal_y)) {
      result.cal_y = search.cal_y;
    }
    if (
      typeof search.cal_m === "number" &&
      Number.isInteger(search.cal_m) &&
      search.cal_m >= 0 &&
      search.cal_m <= 11
    ) {
      result.cal_m = search.cal_m;
    }
    return result;
  },
  // Warm the default (no saved-screen) list + groups for the active date window.
  // Pagination/sort stay in-component (keepPreviousData), so they aren't deps.
  loaderDeps: ({ search }) => ({ since: search.since, until: search.until }),
  loader: ({ context, deps }) => warmRoute(context, "/trades", deps),
});

const createTradeRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/trades/new",
  component: CreateTradePage,
});

// Where the user opened this trade from, so the detail page's back button can
// return there ("Back to dashboard" / "Back to alerts" / "Back to trades").
// Absent / unknown values fall back to the Trades list.
type TradeDetailOrigin = "trades" | "dashboard" | "alerts";
type TradeDetailSearch = { from?: TradeDetailOrigin };
const TRADE_DETAIL_ORIGINS: readonly TradeDetailOrigin[] = ["trades", "dashboard", "alerts"];

const tradeDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/trades/$tradeId",
  component: TradeDetailPage,
  validateSearch: (search: Record<string, unknown>): TradeDetailSearch => {
    const result: TradeDetailSearch = {};
    if (
      typeof search.from === "string" &&
      TRADE_DETAIL_ORIGINS.includes(search.from as TradeDetailOrigin)
    ) {
      result.from = search.from as TradeDetailOrigin;
    }
    return result;
  },
});

const tradeAnalyticsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/trades/$tradeId/analytics",
  component: TradeAnalyticsPage,
});

const tradeWhatIfRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/trades/$tradeId/whatif",
  component: TradeWhatIfPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/dashboard",
  component: DashboardPage,
  validateSearch: (search: Record<string, unknown>) => {
    const base = validateGlobalFilters(search);
    // cal_y/cal_m round-trip the calendar widget's visible month so a "Back to
    // dashboard" return from a day drill-in restores that month.
    const cal: { cal_y?: number; cal_m?: number } = {};
    if (typeof search.cal_y === "number" && Number.isInteger(search.cal_y)) {
      cal.cal_y = search.cal_y;
    }
    if (
      typeof search.cal_m === "number" &&
      Number.isInteger(search.cal_m) &&
      search.cal_m >= 0 &&
      search.cal_m <= 11
    ) {
      cal.cal_m = search.cal_m;
    }
    return search.edit === "1"
      ? { ...base, ...cal, edit: "1" as const }
      : { ...base, ...cal };
  },
  // Edit mode (?edit=1) is intentionally excluded so toggling it never refetches.
  loaderDeps: ({ search }) => ({
    since: search.since,
    until: search.until,
    account_id: search.account_id,
    tag_id: search.tag_id,
  }),
  loader: ({ context, deps }) => warmRoute(context, "/dashboard", deps),
});

// The standalone Reports hub was folded into the Statistics tab (Analytics
// restructure Unit 4): its daily-P&L table now lives there, and its KPI cards
// are superseded by the Overview KPI strip. Redirect stale /reports bookmarks
// to the Statistics tab, preserving any active filter search (account/tag/date
// carried in the URL) so the user's scope survives the hop.
export const reportsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/reports",
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/reports/overall-statistics", search });
  },
});

// Pathless layout route hosting the six Analytics sections behind a shared tab
// strip (ADR 0024 D4). Mirrors the existing `protected` pathless layout: it
// adds no path segment, so its children keep their absolute paths and ids.
const analyticsLayoutRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  id: "analytics-tabs",
  component: AnalyticsLayout,
});

const analyticsRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

const reportsEquityCurveRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: "/reports/equity-curve",
  component: EquityCurvePage,
});

const reportsOverallStatisticsRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: "/reports/overall-statistics",
  component: OverallStatisticsPage,
});

const reportsProfitLossChartsRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: "/reports/profit-loss-charts",
  component: ProfitLossChartsPage,
});

// Win-Rate Charts merged into the "Charts" tab (Analytics restructure Unit 2).
// Redirect stale links to the merged page, preserving the active filter search
// (account/tag/date carried in the URL) so the user's scope survives the hop.
export const reportsWinRateChartsRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: "/reports/win-rate-charts",
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/reports/profit-loss-charts", search });
  },
});

const reportsTagsRoute = createRoute({
  getParentRoute: () => analyticsLayoutRoute,
  path: "/reports/tags",
  component: TagAnalyticsPage,
});

const journalIndexRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/journal",
  beforeLoad: () => {
    throw redirect({ to: "/journal/notes" });
  },
  component: () => null,
});

const journalNotesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/journal/notes",
  component: NotesPage,
});

const journalPlansRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/journal/plans",
  component: () => (
    <ComingSoonPage feature="Trade / Day Plans" plannedUnit="post-batch-1" />
  ),
});

// AI Q&A is in the gated set (ADR 0024 D2) but has no built page, so it is a
// permanent ComingSoon rather than a built-but-gated route behind requireFeature.
// It still sources plannedUnit from the FEATURES map so the gated set stays
// auditable in one place (ADR 0025 D1).
const aiQaRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/ai-qa",
  component: () => (
    <ComingSoonPage
      feature="AI Q&A"
      plannedUnit={FEATURES.aiQa.plannedUnit ?? "post-Phase-8"}
    />
  ),
});

const calendarRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/calendar",
  component: CalendarPage,
  validateSearch: (search: Record<string, unknown>): CalendarSearch => {
    const result: CalendarSearch = {};
    if (typeof search.y === "number" && Number.isInteger(search.y)) {
      result.y = search.y;
    }
    if (
      typeof search.m === "number" &&
      Number.isInteger(search.m) &&
      search.m >= 0 &&
      search.m <= 11
    ) {
      result.m = search.m;
    }
    if (search.view === "month" || search.view === "year") {
      result.view = search.view;
    }
    return result;
  },
});

type AccountsSearch = { newTransaction?: boolean };

const accountsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/accounts",
  component: AccountsPage,
  validateSearch: (search: Record<string, unknown>): AccountsSearch => {
    const result: AccountsSearch = {};
    if (
      search.newTransaction === true ||
      search.newTransaction === "1" ||
      search.newTransaction === "true"
    ) {
      result.newTransaction = true;
    }
    return result;
  },
});

const strategiesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/strategies",
  component: StrategiesPage,
});

const tagsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/tags",
  component: TagsPage,
});

const instrumentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/instruments",
  component: InstrumentsPage,
});

const importsNewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/imports/new",
  component: ImportsNewPage,
});

// Read-only onboarding demo (L0-U10 session 2). A standalone authenticated
// surface that renders a bundled sample dataset; it never queries or mutates
// the real tenant, so demo data is structurally isolated.
const demoRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/demo",
  component: DemoPage,
});

// Alerts is built but gated at launch (ADR 0024 D2). requireFeature renders the
// real pages only when the `alerts` flag is on; otherwise the routes render
// ComingSoonPage so a direct URL cannot reach the gated surface. The sole entry
// point (AlertsBell in the toolbar) is hidden under the same flag (ADR 0025 D3).
const alertsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/alerts",
  component: requireFeature("alerts", AlertsPage, "Alerts"),
});

const alertRulesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/alerts/rules",
  component: requireFeature("alerts", AlertRulesPage, "Alert rules"),
});

const SETTINGS_SECTION_IDS = [
  "profile",
  "trading-defaults",
  "display",
  "getting-started",
  "legal",
] as const;
type SettingsSectionId = (typeof SETTINGS_SECTION_IDS)[number];

const settingsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: "/settings",
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>) => {
    const base = validateGlobalFilters(search);
    const section =
      typeof search.section === "string" &&
      (SETTINGS_SECTION_IDS as ReadonlyArray<string>).includes(search.section)
        ? (search.section as SettingsSectionId)
        : undefined;
    return section === undefined ? base : { ...base, section };
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  callbackRoute,
  termsRoute,
  privacyRoute,
  disclaimerRoute,
  protectedLayoutRoute.addChildren([
    dashboardRoute,
    tradesRoute,
    createTradeRoute,
    tradeDetailRoute,
    tradeAnalyticsRoute,
    tradeWhatIfRoute,
    reportsRoute,
    analyticsLayoutRoute.addChildren([
      analyticsRoute,
      reportsEquityCurveRoute,
      reportsOverallStatisticsRoute,
      reportsProfitLossChartsRoute,
      reportsWinRateChartsRoute,
      reportsTagsRoute,
    ]),
    journalIndexRoute,
    journalNotesRoute,
    journalPlansRoute,
    aiQaRoute,
    calendarRoute,
    accountsRoute,
    strategiesRoute,
    tagsRoute,
    instrumentsRoute,
    importsNewRoute,
    demoRoute,
    alertsRoute,
    alertRulesRoute,
    settingsRoute,
  ]),
]);

// Scroll restoration keyed by pathname (not the default location.href): the
// "Back to calendar" breadcrumb is a forward navigation (new history entry),
// so an href-keyed cache would never match it. Pathname keying restores the
// scroll container for both browser-back AND in-app return links, per page.
// The scrolling element is the AppShell <main>, tagged with
// data-scroll-restoration-id (window itself never scrolls; the shell is
// h-screen overflow-hidden).
export const router = createRouter({
  routeTree,
  // Real instances are injected by <RouterProvider context> in App; this
  // placeholder only satisfies the typed-context requirement at module load.
  context: {
    queryClient: undefined as unknown as QueryClient,
    apiClient: undefined as unknown as ApiClient,
  },
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
  // Catch-all 404 for any unmatched URL (ADR 0025 D2). Rendered at the router
  // root so an unknown path shows a real "not found" surface rather than a blank
  // screen or a misleading "coming soon".
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
